import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { fetchCheckoutStatus } from "../lib/billing";
import { CheckCircle2, Loader2, AlertTriangle, Wallet, ArrowRight } from "lucide-react";
import { useT } from "../lib/i18n";

const POLL_INTERVAL_MS = 2500;
const MAX_ATTEMPTS = 24; // ~60 seconds, gives the webhook plenty of time

function useCheckoutPolling(sessionId) {
  const [state, setState] = useState({ phase: "polling", attempts: 0, data: null, error: null });
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;
    if (!sessionId) {
      setState({ phase: "error", attempts: 0, data: null, error: "Missing session_id" });
      return undefined;
    }

    const poll = async (attempt) => {
      if (cancelled.current) return;
      try {
        const data = await fetchCheckoutStatus(sessionId);
        if (cancelled.current) return;
        if (data.credited && data.status === "paid") {
          setState({ phase: "success", attempts: attempt, data, error: null });
          window.dispatchEvent(new CustomEvent("konzenzo:wallet-updated"));
          return;
        }
        if (data.status === "expired" || data.status === "cancelled") {
          setState({ phase: "error", attempts: attempt, data, error: "Payment was not completed." });
          return;
        }
        if (attempt >= MAX_ATTEMPTS) {
          setState({ phase: "timeout", attempts: attempt, data, error: null });
          return;
        }
        setState({ phase: "polling", attempts: attempt, data, error: null });
        setTimeout(() => poll(attempt + 1), POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled.current) return;
        if (e?.response?.status === 404) {
          setState({ phase: "not_found", attempts: attempt, data: null, error: "Transaction not found." });
          return;
        }
        if (attempt >= MAX_ATTEMPTS) {
          setState({ phase: "error", attempts: attempt, data: null, error: e?.message || "Network error" });
          return;
        }
        setTimeout(() => poll(attempt + 1), POLL_INTERVAL_MS);
      }
    };
    poll(1);
    return () => { cancelled.current = true; };
  }, [sessionId]);

  return state;
}

const PollingCard = ({ attempts, t }) => (
  <div data-testid="billing-success-polling" className="bg-white border border-slate-200 rounded-xl p-10 text-center">
    <Loader2 className="w-10 h-10 text-[#0EA5E9] animate-spin mx-auto" />
    <h2 className="font-display font-black text-2xl text-[#0B1B3D] mt-6">{t("Confirming your payment…")}</h2>
    <p className="text-sm text-[#475569] mt-2">{t("Stripe is finalizing the transaction. Credits are applied only after the verified webhook completes.")}</p>
    <p className="mt-4 font-mono-kz text-xs text-[#94A3B8] tracking-widest">{t("ATTEMPT")} {attempts} / {MAX_ATTEMPTS}</p>
  </div>
);

const SuccessCard = ({ data, navigate, t }) => (
  <div data-testid="billing-success-paid" className="bg-white border border-slate-200 rounded-xl p-10 text-center">
    <div className="mx-auto w-16 h-16 rounded-full bg-[#10B981]/10 flex items-center justify-center">
      <CheckCircle2 className="w-9 h-9 text-[#10B981]" />
    </div>
    <h2 className="font-display font-black text-3xl text-[#0B1B3D] mt-6">{t("Payment successful")}</h2>
    <p className="text-sm text-[#475569] mt-2">{data.pack_name} — €{data.amount_eur} {String(data.currency || "EUR").toUpperCase()}</p>
    <div className="mt-8 bg-[#0B1B3D] text-white rounded-xl p-6 inline-flex items-center gap-4">
      <Wallet className="w-6 h-6 text-[#10B981]" />
      <div className="text-left">
        <p className="font-mono-kz text-xs text-[#10B981] tracking-widest font-bold">{t("CREDITS ADDED")}</p>
        <p className="font-display font-black text-3xl">+{data.credits_added}</p>
        <p className="text-xs text-slate-300 mt-1">
          {t("New balance:")} <span data-testid="success-new-balance" className="font-bold text-white">{data.wallet?.total_balance ?? "—"}</span> {t("credits")}
        </p>
      </div>
    </div>
    <div className="mt-8 flex items-center justify-center gap-3">
      <button data-testid="success-back-to-billing" onClick={() => navigate("/billing")} className="bg-[#0B1B3D] text-white hover:bg-[#1E3A8A] px-5 py-2.5 rounded-lg font-bold text-sm font-display inline-flex items-center gap-2">
        {t("Back to billing")} <ArrowRight className="w-4 h-4" />
      </button>
      <button data-testid="success-go-dashboard" onClick={() => navigate("/dashboard")} className="border border-slate-300 text-[#0B1B3D] hover:bg-slate-50 px-5 py-2.5 rounded-lg font-bold text-sm font-display">
        {t("Go to dashboard")}
      </button>
    </div>
  </div>
);

const TimeoutCard = ({ navigate, t }) => (
  <div data-testid="billing-success-timeout" className="bg-white border border-slate-200 rounded-xl p-10 text-center">
    <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto" />
    <h2 className="font-display font-black text-2xl text-[#0B1B3D] mt-6">{t("Still processing…")}</h2>
    <p className="text-sm text-[#475569] mt-2">{t("Stripe is taking longer than usual to confirm. Your credits will appear as soon as the payment is verified.")}</p>
    <button onClick={() => navigate("/billing")} className="mt-6 bg-[#0B1B3D] text-white hover:bg-[#1E3A8A] px-5 py-2.5 rounded-lg font-bold text-sm font-display">
      {t("View billing")}
    </button>
  </div>
);

const ErrorCard = ({ error, navigate, t }) => (
  <div data-testid="billing-success-error" className="bg-white border border-rose-200 rounded-xl p-10 text-center">
    <AlertTriangle className="w-10 h-10 text-rose-500 mx-auto" />
    <h2 className="font-display font-black text-2xl text-[#0B1B3D] mt-6">{t("We could not confirm your payment")}</h2>
    <p className="text-sm text-[#475569] mt-2">{error || "Please contact support."}</p>
    <button onClick={() => navigate("/billing")} className="mt-6 bg-[#0B1B3D] text-white hover:bg-[#1E3A8A] px-5 py-2.5 rounded-lg font-bold text-sm font-display">
      {t("Back to billing")}
    </button>
  </div>
);

export default function BillingSuccess() {
  const { t } = useT();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const state = useCheckoutPolling(params.get("session_id"));

  return (
    <DashboardLayout title={t("Payment confirmation")}>
      <div className="max-w-2xl mx-auto">
        {state.phase === "polling" && <PollingCard attempts={state.attempts} t={t} />}
        {state.phase === "success" && state.data && <SuccessCard data={state.data} navigate={navigate} t={t} />}
        {state.phase === "timeout" && <TimeoutCard navigate={navigate} t={t} />}
        {(state.phase === "error" || state.phase === "not_found") && <ErrorCard error={state.error} navigate={navigate} t={t} />}
      </div>
    </DashboardLayout>
  );
}
